import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Typography,
  OutlinedInput,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close,
  FilterList,
  Clear,
  Search,
  Sort,
  Delete,
  Add,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  FilterModalActions,
  FilterModalHeader,
  filterModalContentSx,
  filterModalPaperProps,
} from "components/FilterModalChrome";

const CustomFilterModal = ({
  open,
  onClose,
  onApplyFilter,
  onClearFilter,
  data = [],
  sortableColumns = [],
}) => {
  const theme = useTheme();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedShift, setSelectedShift] = useState([]);
  const [selectedLokasi, setSelectedLokasi] = useState([]);

  // Sort state: array of { column, order }
  const [sortCriteria, setSortCriteria] = useState([
    { column: "", order: "asc" },
  ]);

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedStatus([]);
    setSelectedShift([]);
    setSelectedLokasi([]);
    setSortCriteria([{ column: "", order: "asc" }]);
    onClearFilter();
  };

  const getUniqueStatusAbsensi = () => {
    return [
      ...new Set(
        data
          .map((row) => row.status)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  const getUniqueShiftAbsen = () => {
    return [
      ...new Set(
        data
          .map((row) => row.shift_absen)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  const getUniqueLokasi = () => {
    return [
      ...new Set(
        data
          .map((row) => row.location_check_in)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  // Hanya tampilkan kolom yang belum dipilih
  const getAvailableColumns = (currentIndex) => {
    const usedColumns = sortCriteria
      .filter((_, idx) => idx !== currentIndex)
      .map((c) => c.column);
    return sortableColumns.filter((col) => !usedColumns.includes(col.value));
  };

  const handleSortChange = (index, key, value) => {
    const updated = [...sortCriteria];
    updated[index][key] = value;
    setSortCriteria(updated);
  };

  const addSortCriteria = () => {
    setSortCriteria([...sortCriteria, { column: "", order: "asc" }]);
  };

  const removeSortCriteria = (index) => {
    const updated = [...sortCriteria];
    updated.splice(index, 1);
    setSortCriteria(updated.length ? updated : [{ column: "", order: "asc" }]);
  };

  const applyFilters = () => {
    const filterConfig = {
      search: searchQuery,
      filter: {
        status: selectedStatus,
        shift_absen: selectedShift,
        location_check_in: selectedLokasi,
      },
      sort: sortCriteria.filter((c) => c.column), // hanya kirim kriteria valid
    };
    onApplyFilter(filterConfig);
    onClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedStatus.length > 0) count++;
    if (selectedShift.length > 0) count++;
    if (selectedLokasi.length > 0) count++;
    if (sortCriteria.some((c) => c.column)) count++;
    return count;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={filterModalPaperProps(theme)}
    >
      <FilterModalHeader title="Filter Data Absensi Hari Ini" onClose={onClose} />

      <DialogContent
        sx={filterModalContentSx(theme)}
      >
        {/* SEARCH */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
            🔍 Search
          </Typography>
          <TextField
            fullWidth
            label="Cari ID atau Nama Karyawan"
            placeholder="Ketik ID atau Nama Karyawan..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            size="small"
          />
        </Box>

        <Divider />

        {/* FILTER */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            🧭 Filter
          </Typography>
          <Grid container spacing={3}>
            {/* Status */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Status</InputLabel>
                <Select
                  multiple
                  value={selectedStatus}
                  onChange={(e) =>
                    setSelectedStatus(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Filter Status" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getUniqueStatusAbsensi().map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={selectedStatus.indexOf(item) > -1} />
                      <ListItemText primary={item} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Shift */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Shift</InputLabel>
                <Select
                  multiple
                  value={selectedShift}
                  onChange={(e) =>
                    setSelectedShift(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Filter Shift" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getUniqueShiftAbsen().map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={selectedShift.indexOf(item) > -1} />
                      <ListItemText primary={item} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* lokasi */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Lokasi</InputLabel>
                <Select
                  multiple
                  value={selectedLokasi}
                  onChange={(e) =>
                    setSelectedLokasi(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Filter Lokasi" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getUniqueLokasi().map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={selectedLokasi.indexOf(item) > -1} />
                      <ListItemText primary={item} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* SORT */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            ↕️ Sort
          </Typography>
          {sortCriteria.map((criteria, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kriteria {index + 1}</InputLabel>
                  <Select
                    value={criteria.column}
                    label={`Kriteria ${index + 1}`}
                    onChange={(e) =>
                      handleSortChange(index, "column", e.target.value)
                    }
                  >
                    <MenuItem value="">
                      <em>Pilih Kolom</em>
                    </MenuItem>
                    {getAvailableColumns(index).map((col) => (
                      <MenuItem key={col.value} value={col.value}>
                        {col.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!criteria.column}>
                  <InputLabel>Urutan</InputLabel>
                  <Select
                    value={criteria.order}
                    label="Urutan"
                    onChange={(e) =>
                      handleSortChange(index, "order", e.target.value)
                    }
                  >
                    <MenuItem value="asc">Menaik</MenuItem>
                    <MenuItem value="desc">Menurun</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {sortCriteria.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => removeSortCriteria(index)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          {getAvailableColumns(-1).length > 0 && (
            <Button
              startIcon={<Add />}
              size="small"
              variant="contained"
              onClick={addSortCriteria}
              color="primary"
            >
              Tambah Kriteria Sort
            </Button>
          )}
        </Box>
      </DialogContent>

      <FilterModalActions
        onClearFilter={clearAllFilters}
        onClose={onClose}
        onApply={applyFilters}
        activeCount={getActiveFilterCount()}
      />
    </Dialog>
  );
};

export default CustomFilterModal;
